import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Order } from '../../../store/order/api/interface';
import { Order as OrderService } from '../../../client/services';
import { toastHandler } from '../../../utils/toastHandler';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { customIcon } from '../../../utils/icon';

interface RecipientLocationEditDialogProps {
  open: boolean;
  onClose: () => void;
  order: Order | null | undefined;
  onLocationUpdate: (updatedOrder: Order) => void;
}

interface LocationData {
  latitude: number | null;
  longitude: number | null;
}

// Map click handler component
const MapClickHandler: React.FC<{ onLocationSelect: (lat: number, lng: number) => void }> = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      const { lat, lng } = e.latlng;
      onLocationSelect(lat, lng);
    },
  });
  return null;
};

const RecipientLocationEditDialog: React.FC<RecipientLocationEditDialogProps> = ({
  open,
  onClose,
  order,
  onLocationUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<LocationData>({
    latitude: null,
    longitude: null,
  });

  // Initialize form data when dialog opens
  useEffect(() => {
    if (open && order) {
      const shippingAddress = order.shippingAddress as any || {};
      
      setFormData({
        latitude: shippingAddress.latitude || 25.3548, // Default to Qatar coordinates
        longitude: shippingAddress.longitude || 51.1839,
      });
      setError(null);
    }
  }, [open, order]);

  const handleInputChange = (field: keyof LocationData, value: string) => {
    const numValue = value === '' ? null : parseFloat(value);
    setFormData(prev => ({
      ...prev,
      [field]: numValue,
    }));
  };

  const handleMapClick = (lat: number, lng: number) => {
    setFormData({
      latitude: lat,
      longitude: lng,
    });
  };

  const validateForm = (): boolean => {
    if (formData.latitude === null || formData.longitude === null) {
      setError('Both latitude and longitude are required');
      return false;
    }
    if (typeof formData.latitude !== 'number' || typeof formData.longitude !== 'number') {
      setError('Invalid coordinate values');
      return false;
    }
    if (formData.latitude < -90 || formData.latitude > 90) {
      setError('Latitude must be between -90 and 90');
      return false;
    }
    if (formData.longitude < -180 || formData.longitude > 180) {
      setError('Longitude must be between -180 and 180');
      return false;
    }
    setError(null);
    return true;
  };

  const handleSave = async () => {
    if (!validateForm() || !order) return;

    setLoading(true);
    setError(null);

    try {
      // Prepare shipping address with updated coordinates
      const shippingAddress = {
        ...order.shippingAddress,
        latitude: Number(formData.latitude),
        longitude: Number(formData.longitude),
      };

      // Update order with new coordinates
      const updateData = {
        shippingAddress: JSON.stringify(shippingAddress),
      };

      console.log('Updating map location:', updateData);

      const response = await OrderService.patch(order.id, updateData);
      
      if (response) {
        // Update the order with new data
        const updatedOrder = {
          ...order,
          shippingAddress: {
            ...shippingAddress,
            latitude: Number(formData.latitude),
            longitude: Number(formData.longitude),
          },
        };
        
        onLocationUpdate(updatedOrder);
        onClose();
        
        // Show success message
        toastHandler({
          message: 'Map location updated successfully',
          type: 'success',
        });
      }
    } catch (error: any) {
      console.error('Error updating map location:', error);
      setError(error.message || 'Failed to update map location');
      toastHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Edit Map Location</Typography>
        <Typography variant="body2" color="text.secondary">
          Click on the map to select the delivery location coordinates
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click anywhere on the map below to set the delivery location coordinates, or enter them manually in the fields below.
          </Typography>
          
           {/* Interactive Map */}
           <Box sx={{ height: 400, width: '100%', mb: 2, border: '1px solid #ddd', borderRadius: 1 }}>
             <MapContainer
               center={[
                 typeof formData.latitude === 'number' ? formData.latitude : 25.3548, 
                 typeof formData.longitude === 'number' ? formData.longitude : 51.1839
               ]}
               zoom={13}
               style={{ height: '100%', width: '100%' }}
               attributionControl={false}
             >
               <TileLayer url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' />
               <MapClickHandler onLocationSelect={handleMapClick} />
               {formData.latitude !== null && formData.longitude !== null && 
                typeof formData.latitude === 'number' && typeof formData.longitude === 'number' && (
                 <Marker 
                   position={[formData.latitude, formData.longitude]}
                   icon={customIcon}
                 />
               )}
             </MapContainer>
           </Box>

          {/* Coordinate Input Fields */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Latitude"
                type="number"
                value={formData.latitude || ''}
                onChange={(e) => handleInputChange('latitude', e.target.value)}
                required
                disabled={loading}
                inputProps={{
                  step: "any",
                  min: -90,
                  max: 90
                }}
                helperText="Range: -90 to 90"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Longitude"
                type="number"
                value={formData.longitude || ''}
                onChange={(e) => handleInputChange('longitude', e.target.value)}
                required
                disabled={loading}
                inputProps={{
                  step: "any",
                  min: -180,
                  max: 180
                }}
                helperText="Range: -180 to 180"
              />
            </Grid>
          </Grid>

          {formData.latitude !== null && formData.longitude !== null && 
           typeof formData.latitude === 'number' && typeof formData.longitude === 'number' && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Selected Location:</strong> {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <a 
                  href={`https://www.google.com/maps?q=${formData.latitude},${formData.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View on Google Maps
                </a>
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Saving...' : 'Save Location'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecipientLocationEditDialog;
